// libs
import React, { ReactElement } from "react";

// internal libs
import { RouterProps, Router } from "@swiggy-private/core-ui";
import { CheckoutRoute } from "@swiggy-private/checkout-core";

// components
import { ConfirmOrder } from "@payment-client/components/confirm-order";
import { DirectPlaceOrder } from "@payment-client/components/direct-place-order";
import { VpaTimer } from "@payment-client/components/vpa-timer";
import { ShowPayments } from "@payment-client/components/show-payments";
import { PaymentRenderer } from "@payment-client/components/show-payments/components/payment-renderer";
import { LinkWalletWithNumber } from "@payment-client/components/show-payments/components/link-wallet-with-number";
import { VerifyWalletLinkOTP } from "@payment-client/components/show-payments/components/verify-wallet-link-otp";
import { PaymentLinkPayLater } from "@payment-client/components/show-payments/components/link-paylater";
import LinkPaylaterFullPage from "@payment-client/components/show-payments/components/link-paylater/link-full-page-view/LinkFullPage";
import { AddNewCardPayment } from "@payment-client/components/show-payments/components/add-new-card";
import { PaymentVerifyVpa } from "@payment-client/components/show-payments/components/verify-vpa";
import { NetBankingMoreBanks } from "@payment-client/components/show-payments/components/more-banks";
import { MorePaymentGroups } from "@payment-client/components/show-payments/components/more-payment-groups";
import { MorePaymentMethods } from "@payment-client/components/show-payments/components/more-payment-methods";
import { RedirectPage } from "@payment-client/components/show-payments/components/redirect-page";
import { PaymentTxnStatusPoller } from "@payment-client/components/upi-intent-poller";
import { SwiggyMoneyPaymentsContainer } from "@payment-client/components/show-payments/components/swiggy-money-payments/SwiggyMoneyPaymentsContainer";
import { AddSwiggyMoneyContainer } from "@payment-client/components/show-payments/components/swiggy-money-payments";
import AutopayPaymentFailureScreen from "@payment-client/components/autopay-payment-failure-screen/AutopayPaymentFailureScreen";
import { PaymentOffers } from "@payment-client/components/show-payments/components/payment-offers-renderer";
import { EmiSelection } from "@payment-client/components/emi-selection/EmiSelection";

import { RouteContainer } from "./RouteContainer";
import { TurboPlaceOrder } from "@payment-client/components/turbo-place-order";
import { LinkPayLaterWithNumber } from "@payment-client/components/show-payments/components/link-paylater-with-number";
import ReviewBillDetailsRenderer from "@payment-client/components/show-payments/components/review-bill-details-renderer";

export type PaymentRouterProps = Omit<RouterProps<CheckoutRoute>, "children">;

// These routes need out-in transition since they
// are routed via state and not via user action
// Because of in-out transitions we were facing issue of double mounts
export const OUT_IN_ROUTES = [CheckoutRoute.CONFIRM_ORDER, CheckoutRoute.PAYMENT_TXN_STATUS_POLLING];

export function PaymentRouter(props: PaymentRouterProps): ReactElement {
    return (
        <Router {...props} container={RouteContainer} nonOverlappingTransitionRoutes={OUT_IN_ROUTES}>
            <ShowPayments
                name={CheckoutRoute.PAYMENTS}
                indexElement={<PaymentRenderer name={CheckoutRoute.PAYMENTS_INDEX} />}
            >
                <VerifyWalletLinkOTP name={CheckoutRoute.VERIFY_WALLET_LINK_OTP} />
                <LinkWalletWithNumber name={CheckoutRoute.LINK_WALLET_WITH_NUMBER} />
                <PaymentLinkPayLater name={CheckoutRoute.LINK_PAYLATER} />
                <LinkPayLaterWithNumber name={CheckoutRoute.LINK_PAYLATER_MOBILE} />
                <AddNewCardPayment name={CheckoutRoute.ADD_NEW_CARD} />
                <PaymentVerifyVpa name={CheckoutRoute.VERIFY_VPA} />
                <NetBankingMoreBanks name={CheckoutRoute.MORE_BANKS} />
                <VpaTimer name={CheckoutRoute.VPA_TIMER} />
                <PaymentTxnStatusPoller name={CheckoutRoute.PAYMENT_TXN_STATUS_POLLING} />
                <MorePaymentGroups name={CheckoutRoute.MORE_PAYMENT_GROUPS} />
                <MorePaymentMethods name={CheckoutRoute.MORE_PAYMENT_METHODS} />
<AutopayPaymentFailureScreen name={CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED} />
                <LinkPaylaterFullPage name={CheckoutRoute.PAYLATER_LINKING_CONFIRMATION} />
                <TurboPlaceOrder name={CheckoutRoute.TURBO_PLACE_ORDER} key={CheckoutRoute.TURBO_PLACE_ORDER} />
                <PaymentOffers name={CheckoutRoute.PAYMENT_OFFERS} />
                <EmiSelection name={CheckoutRoute.EMI_SELECTION} />
                <ReviewBillDetailsRenderer name={CheckoutRoute.REVIEW_ORDER_DETAILS} />
            </ShowPayments>

            <SwiggyMoneyPaymentsContainer
                name={CheckoutRoute.ADD_SWIGGY_MONEY}
                indexElement={<AddSwiggyMoneyContainer name={CheckoutRoute.ADD_SWIGGY_MONEY_INDEX} />}
            >
                <ShowPayments
                    name={CheckoutRoute.SWIGGY_MONEY_PAYMENTS}
                    indexElement={<PaymentRenderer name={CheckoutRoute.SWIGGY_MONEY_PAYMENTS} />}
                >
                    <AddNewCardPayment name={CheckoutRoute.ADD_NEW_CARD} />
                    <NetBankingMoreBanks name={CheckoutRoute.MORE_BANKS} />
                    <PaymentTxnStatusPoller name={CheckoutRoute.PAYMENT_TXN_STATUS_POLLING} />
                    <MorePaymentMethods name={CheckoutRoute.MORE_PAYMENT_METHODS} />
                </ShowPayments>
            </SwiggyMoneyPaymentsContainer>

            <ConfirmOrder name={CheckoutRoute.CONFIRM_ORDER} />
            <DirectPlaceOrder name={CheckoutRoute.DIRECT_PLACE_ORDER} />
            <RedirectPage name={CheckoutRoute.REDIRECT_PAGE} />
        </Router>
    );
}
